import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  RadialBarChart,
  RadialBar,
  Legend,
} from 'recharts';
import {
  Users, Megaphone, GraduationCap, FileText, TrendingUp, ArrowUpRight,
  Sparkles, Activity, Target,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Soft, pastel palette
const P = {
  indigo: '#6366f1',
  indigoSoft: '#eef2ff',
  amber: '#f59e0b',
  amberSoft: '#fef3c7',
  emerald: '#10b981',
  emeraldSoft: '#d1fae5',
  rose: '#f43f5e',
  roseSoft: '#ffe4e6',
  sky: '#0ea5e9',
  skySoft: '#e0f2fe',
  navy: '#1b2a6b',
  gold: '#c8a035',
};

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 0.9, 0.32, 1] },
  }),
};

function CountUp({ to, suffix = '' }) {
  const mv = useMotionValue(0);
  const [val, setVal] = useState(0);
  useEffect(() => {
    const controls = animate(mv, to, { duration: 1.3, ease: [0.22, 0.9, 0.32, 1] });
    const unsub = mv.on('change', (v) => setVal(Math.round(v)));
    return () => { controls.stop(); unsub(); };
  }, [to, mv]);
  return <>{val}{suffix}</>;
}

export default function AdminOverview() {
  const [data, setData] = useState({ recruiters: [], leads: [], colleges: [], students: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/admin/recruiters`, { credentials: 'include' }).then((r) => r.ok ? r.json() : { recruiters: [] }),
      fetch(`${API}/api/admin/leads`, { credentials: 'include' }).then((r) => r.ok ? r.json() : { leads: [] }),
      fetch(`${API}/api/admin/colleges`, { credentials: 'include' }).then((r) => r.ok ? r.json() : { colleges: [] }),
      fetch(`${API}/api/admin/students`, { credentials: 'include' }).then((r) => r.ok ? r.json() : { students: [] }),
    ]).then(([rec, leads, col, stu]) => {
      setData({
        recruiters: rec.recruiters || [],
        leads: leads.leads || [],
        colleges: col.colleges || [],
        students: stu.students || [],
      });
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-tge-navy" />
      </div>
    );
  }

  const stats = [
    { label: 'Total Recruiters', value: data.recruiters.length, Icon: Users, tint: P.indigo, bg: P.indigoSoft, delta: '+12%' },
    { label: 'Active Leads', value: data.leads.length, Icon: Megaphone, tint: P.amber, bg: P.amberSoft, delta: '+34%' },
    { label: 'Partner Colleges', value: data.colleges.length, Icon: GraduationCap, tint: P.emerald, bg: P.emeraldSoft, delta: '+5%' },
    { label: 'Student Applications', value: data.students.length, Icon: FileText, tint: P.sky, bg: P.skySoft, delta: '+19%' },
  ];

  const leadsBySource = aggregate(data.leads, 'source', { meta: 'Meta', google: 'Google', manual: 'Manual' });
  const leadsByStatus = aggregate(data.leads, 'status', {
    new: 'New', contacted: 'Contacted', qualified: 'Qualified', converted: 'Converted', lost: 'Lost',
  });
  const recruitersByStatus = aggregate(data.recruiters, 'status', {
    pending: 'Pending', signed: 'Signed', active: 'Active',
  });
  const series = timeSeries([...data.leads, ...data.students], 14);

  // Conversion funnel
  const funnelTotal = data.leads.length || 1;
  const funnelQualified = data.leads.filter((l) => ['qualified', 'converted'].includes(l.status)).length;
  const funnelConverted = data.leads.filter((l) => l.status === 'converted').length;
  const conversionRate = Math.round((funnelConverted / funnelTotal) * 100);
  const qualifyRate = Math.round((funnelQualified / funnelTotal) * 100);

  return (
    <div className="space-y-6" data-testid="admin-overview">
      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <motion.div
            key={s.label}
            custom={i}
            initial="hidden"
            animate="show"
            variants={fadeUp}
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            className="relative bg-white rounded-2xl p-5 shadow-[0_1px_3px_rgba(27,42,107,0.04),_0_4px_20px_rgba(27,42,107,0.03)] overflow-hidden"
          >
            <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full opacity-40" style={{ backgroundColor: s.bg }} />
            <div className="relative">
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: s.bg, color: s.tint }}>
                  <s.Icon size={20} strokeWidth={1.8} />
                </div>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#ecfdf5', color: P.emerald }}>
                  <ArrowUpRight size={10} className="inline mr-0.5" />{s.delta}
                </span>
              </div>
              <div className="mt-5">
                <div className="font-serif-display text-4xl text-tge-navy">
                  <CountUp to={s.value} />
                </div>
                <div className="text-[11px] uppercase tracking-[0.12em] text-tge-muted mt-1.5 font-medium">{s.label}</div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Activity chart + Channel mix */}
      <div className="grid lg:grid-cols-3 gap-4">
        <motion.div custom={1} variants={fadeUp} initial="hidden" animate="show" className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-[0_1px_3px_rgba(27,42,107,0.04)]">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-tge-muted font-semibold">Last 14 days</div>
              <h3 className="font-serif-display text-2xl text-tge-navy mt-1">Pipeline Activity</h3>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <LegendItem color={P.amber} label="Leads" />
              <LegendItem color={P.indigo} label="Applications" />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={series} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gradAmber" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={P.amber} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={P.amber} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradIndigo" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={P.indigo} stopOpacity={0.2} />
                  <stop offset="100%" stopColor={P.indigo} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1ece0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#8b8f99' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#8b8f99' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="Leads" stroke={P.amber} strokeWidth={2.5} fill="url(#gradAmber)" />
              <Area type="monotone" dataKey="Applications" stroke={P.indigo} strokeWidth={2.5} fill="url(#gradIndigo)" />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        <motion.div custom={2} variants={fadeUp} initial="hidden" animate="show" className="bg-white rounded-2xl p-6 shadow-[0_1px_3px_rgba(27,42,107,0.04)]">
          <div className="text-[11px] uppercase tracking-[0.15em] text-tge-muted font-semibold">Leads by source</div>
          <h3 className="font-serif-display text-2xl text-tge-navy mt-1 mb-3">Channel Mix</h3>
          {leadsBySource.length === 0 ? (
            <Empty text="No leads yet" />
          ) : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={leadsBySource} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={78} paddingAngle={4}>
                    {leadsBySource.map((_, i) => <Cell key={i} fill={[P.indigo, P.amber, P.emerald, P.rose][i % 4]} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {leadsBySource.map((s, i) => (
                  <div key={s.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: [P.indigo, P.amber, P.emerald, P.rose][i % 4] }} />
                      <span className="text-tge-navy font-medium">{s.name}</span>
                    </div>
                    <span className="text-tge-muted">{s.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </motion.div>
      </div>

      {/* Lead Funnel + Status bars + Recruiter onboarding */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Funnel radial chart */}
        <motion.div custom={3} variants={fadeUp} initial="hidden" animate="show" className="bg-white rounded-2xl p-6 shadow-[0_1px_3px_rgba(27,42,107,0.04)]">
          <div className="text-[11px] uppercase tracking-[0.15em] text-tge-muted font-semibold">Conversion</div>
          <h3 className="font-serif-display text-2xl text-tge-navy mt-1 mb-4">Lead Funnel</h3>
          <div className="relative">
            <ResponsiveContainer width="100%" height={180}>
              <RadialBarChart cx="50%" cy="50%" innerRadius="55%" outerRadius="90%" data={[
                { name: 'Qualified', value: qualifyRate, fill: P.amber },
                { name: 'Converted', value: conversionRate, fill: P.emerald },
              ]} startAngle={90} endAngle={-270}>
                <RadialBar dataKey="value" cornerRadius={12} background={{ fill: '#f1ece0' }} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="font-serif-display text-3xl text-tge-navy"><CountUp to={conversionRate} suffix="%" /></div>
              <div className="text-[10px] uppercase tracking-wider text-tge-muted">conversion</div>
            </div>
          </div>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-tge-cream">
            <div className="text-center flex-1">
              <div className="text-xl font-serif-display text-amber-600">{qualifyRate}%</div>
              <div className="text-[10px] uppercase text-tge-muted tracking-wider">qualified</div>
            </div>
            <div className="w-px h-8 bg-tge-cream" />
            <div className="text-center flex-1">
              <div className="text-xl font-serif-display text-emerald-600">{conversionRate}%</div>
              <div className="text-[10px] uppercase text-tge-muted tracking-wider">converted</div>
            </div>
          </div>
        </motion.div>

        {/* Status bars */}
        <motion.div custom={4} variants={fadeUp} initial="hidden" animate="show" className="bg-white rounded-2xl p-6 shadow-[0_1px_3px_rgba(27,42,107,0.04)]">
          <div className="text-[11px] uppercase tracking-[0.15em] text-tge-muted font-semibold">Lead pipeline</div>
          <h3 className="font-serif-display text-2xl text-tge-navy mt-1 mb-4">By Status</h3>
          {leadsByStatus.length === 0 ? <Empty text="No leads yet" /> : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={leadsByStatus} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1ece0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#8b8f99' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#8b8f99' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="value" radius={[10, 10, 0, 0]}>
                  {leadsByStatus.map((_, i) => (
                    <Cell key={i} fill={[P.sky, P.indigo, P.amber, P.emerald, P.rose][i]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </motion.div>

        {/* Recruiter animated progress */}
        <motion.div custom={5} variants={fadeUp} initial="hidden" animate="show" className="bg-white rounded-2xl p-6 shadow-[0_1px_3px_rgba(27,42,107,0.04)]">
          <div className="text-[11px] uppercase tracking-[0.15em] text-tge-muted font-semibold">Partners</div>
          <h3 className="font-serif-display text-2xl text-tge-navy mt-1 mb-4">Onboarding</h3>
          {recruitersByStatus.length === 0 ? <Empty text="No recruiters yet" /> : (
            <div className="space-y-5 mt-2">
              {recruitersByStatus.map((s, i) => {
                const total = recruitersByStatus.reduce((a, b) => a + b.value, 0) || 1;
                const pct = Math.round((s.value / total) * 100);
                const c = [P.amber, P.sky, P.emerald][i] || P.indigo;
                return (
                  <div key={s.name}>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-tge-navy font-medium">{s.name}</span>
                      <span className="text-tge-muted text-xs">
                        <span className="font-serif-display text-base text-tge-navy mr-1">{s.value}</span>
                        · {pct}%
                      </span>
                    </div>
                    <div className="h-2.5 bg-tge-cream-soft rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 1, delay: 0.3 + i * 0.15, ease: [0.22, 0.9, 0.32, 1] }}
                        className="h-full rounded-full"
                        style={{ backgroundColor: c, boxShadow: `0 0 12px ${c}55` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>

      {/* Highlights banner */}
      <motion.div custom={6} variants={fadeUp} initial="hidden" animate="show"
        className="rounded-2xl p-6 text-white relative overflow-hidden shadow-[0_10px_30px_rgba(15,30,84,0.25)]"
        style={{ background: 'linear-gradient(90deg, #1b2a6b 0%, #0f1e54 60%, #1e1b4b 100%)' }}
      >
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-tge-gold/20 blur-3xl" />
        <div className="absolute -left-10 -bottom-10 w-40 h-40 rounded-full bg-sky-400/15 blur-3xl" />
        <div className="relative grid sm:grid-cols-3 gap-6">
          <HighlightCell Icon={Sparkles} label="Best source" value={leadsBySource[0]?.name || '—'} hint={leadsBySource[0] ? `${leadsBySource[0].value} leads` : ''} />
          <HighlightCell Icon={Activity} label="This week" value={`${series.slice(-7).reduce((a, b) => a + b.Leads, 0)} leads`} hint="7-day rolling" />
          <HighlightCell Icon={Target} label="Top stage" value={(leadsByStatus.sort((a, b) => b.value - a.value)[0]?.name) || '—'} hint="by volume" />
        </div>
      </motion.div>
    </div>
  );
}

function HighlightCell({ Icon, label, value, hint }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-tge-gold">
        <Icon size={18} />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-white/60 font-semibold">{label}</div>
        <div className="font-serif-display text-xl mt-0.5">{value}</div>
        {hint && <div className="text-xs text-white/60 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

function LegendItem({ color, label }) {
  return (
    <div className="flex items-center gap-1.5 text-tge-muted">
      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </div>
  );
}

function Empty({ text }) {
  return <div className="h-[180px] flex items-center justify-center text-sm text-tge-muted">{text}</div>;
}

const tooltipStyle = {
  borderRadius: 12,
  border: 'none',
  boxShadow: '0 8px 24px rgba(27,42,107,0.1)',
  fontSize: 12,
};

function aggregate(items, key, labels) {
  const map = {};
  for (const it of items) {
    const k = it[key] || 'unknown';
    map[k] = (map[k] || 0) + 1;
  }
  return Object.entries(map).map(([k, v]) => ({ name: labels[k] || k, value: v }));
}

function timeSeries(items, days) {
  const out = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    out.push({
      key: d.toISOString().slice(0, 10),
      date: d.toLocaleDateString('en-CA', { timeZone: 'America/Vancouver', month: 'short', day: 'numeric' }),
      Leads: 0, Applications: 0,
    });
  }
  for (const it of items) {
    const k = (it.created_at || '').slice(0, 10);
    const row = out.find((r) => r.key === k);
    if (!row) continue;
    if (it.source !== undefined) row.Leads += 1;
    else row.Applications += 1;
  }
  return out;
}
